/**
 * Standardized API Response Types
 *
 * These types match the backend API response format for type-safe
 * handling of all API responses.
 */

// =============================================================================
// Response Envelope Types
// =============================================================================

/**
 * Root response envelope - ALL API responses use this structure
 */
export interface ApiResponse<T = unknown> {
  /** Indicates if the request was successful */
  success: boolean;

  /** Present on success (success: true) */
  data?: T;

  /** Present on failure (success: false) */
  error?: ApiError;

  /** Optional metadata applicable to both success and error */
  meta?: ResponseMeta;
}

/**
 * Response metadata
 */
export interface ResponseMeta {
  /** API version for tracking/debugging */
  apiVersion?: string;

  /** Request ID for tracing */
  requestId?: string;

  /** ISO 8601 timestamp */
  timestamp?: string;

  /** Pagination info (when applicable) */
  pagination?: PaginationMeta;
}

/**
 * Pagination metadata for collection responses
 */
export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// =============================================================================
// Success Response Types
// =============================================================================

/** Single resource success response */
export interface SuccessResponse<T> extends ApiResponse<T> {
  success: true;
  data: T;
  error?: never;
}

/** Collection success response with pagination */
export interface CollectionResponse<T> extends ApiResponse<T[]> {
  success: true;
  data: T[];
  error?: never;
  meta?: ResponseMeta & {
    pagination: PaginationMeta;
  };
}

// =============================================================================
// Error Response Types
// =============================================================================

/** Error response structure */
export interface ErrorResponse extends ApiResponse<never> {
  success: false;
  data?: never;
  error: ApiError;
}

/**
 * API Error structure
 */
export interface ApiError {
  /** HTTP status code */
  code: number;

  /** Machine-readable error type */
  type: string;

  /** Human-readable, user-friendly summary */
  message: string;

  /** Detailed, developer-focused explanation */
  detail?: string;

  /** Array of specific violations (for validation errors) */
  violations?: Violation[];

  /** Unique identifier for this specific error occurrence */
  instance?: string;

  /** Link to documentation about this error type */
  helpUrl?: string;
}

/**
 * Validation violation details
 */
export interface Violation {
  /** JSON Pointer to the field with the issue */
  field: string;

  /** Machine-readable violation code */
  code: string;

  /** Human-readable description of the violation */
  message: string;

  /** The rejected value (optional) */
  rejectedValue?: unknown;

  /** Constraint details that were violated */
  constraint?: {
    type: string;
    params?: Record<string, unknown>;
  };
}

// =============================================================================
// Error Type Constants
// =============================================================================

export const ERROR_TYPES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTHENTICATION_REQUIRED: 'AUTHENTICATION_REQUIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
  CONFLICT: 'CONFLICT',
  RESOURCE_ALREADY_EXISTS: 'RESOURCE_ALREADY_EXISTS',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
} as const;

export type ErrorType = typeof ERROR_TYPES[keyof typeof ERROR_TYPES];

// =============================================================================
// Violation Code Constants
// =============================================================================

export const VIOLATION_CODES = {
  INVALID_FORMAT: 'INVALID_FORMAT',
  INVALID_TYPE: 'INVALID_TYPE',
  INVALID_ENUM: 'INVALID_ENUM',
  TOO_SHORT: 'TOO_SHORT',
  TOO_LONG: 'TOO_LONG',
  TOO_FEW_ITEMS: 'TOO_FEW_ITEMS',
  TOO_MANY_ITEMS: 'TOO_MANY_ITEMS',
  OUT_OF_RANGE: 'OUT_OF_RANGE',
  TOO_LOW: 'TOO_LOW',
  TOO_HIGH: 'TOO_HIGH',
  REQUIRED: 'REQUIRED',
  NOT_NULLABLE: 'NOT_NULLABLE',
  NOT_ALLOWED: 'NOT_ALLOWED',
  DUPLICATE: 'DUPLICATE',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  REFERENCE_NOT_FOUND: 'REFERENCE_NOT_FOUND',
  INVALID_REFERENCE: 'INVALID_REFERENCE',
  BUSINESS_RULE_VIOLATION: 'BUSINESS_RULE_VIOLATION',
  STATE_CONFLICT: 'STATE_CONFLICT',
} as const;

export type ViolationCode = typeof VIOLATION_CODES[keyof typeof VIOLATION_CODES];
